import { Injectable, inject } from "@angular/core";
import html2canvas from "html2canvas";
import { MapService } from "./map.service";
import {MapInteractionService} from "../../../ai-components/chat/services/map-interaction.service";

/**
 * Encapsulates map canvas capture logic, decoupled from ScreenshotComponent.
 *
 * Provided at PhoenixViewerContainerComponent scope (same as MapService) so it
 * is always instantiated alongside the map, regardless of whether ScreenshotComponent
 * is included in the template. The constructor auto-registers captureMapCanvas as the
 * screenshot provider in MapInteractionService, making it available to the
 * get_screenshot AI frontend tool without any dependency on ScreenshotComponent.
 */
@Injectable()
export class MapCanvasService {
  private readonly mapService = inject(MapService);
  private readonly mapInteraction = inject(MapInteractionService);

  constructor() {
    this.mapInteraction.registerScreenshotProvider(async () => {
      const coreMap = this.mapService.getCoreMap();
      // Read the view state before the async capture so it matches what is rendered.
      const bbox = [...coreMap.getExtent()];
      const zoom = coreMap.getZoom();
      const blob = await this.captureMapCanvas();
      return { blob, bbox, zoom };
    });
  }

  /**
   * Renders the map and composites all OL canvas layers, measurement overlays,
   * and location info into a single PNG Blob.
   *
   * Called by ScreenshotComponent (download button) and by the get_screenshot
   * AI frontend tool via MapInteractionService.captureScreenshot().
   */
  async captureMapCanvas(): Promise<Blob> {
    const map = this.mapService.getCoreMap().olMap;
    const size = map.getSize();
    const mapId = this.mapService.getMapId();

    map.renderSync();

    const mapCanvas = document.createElement("canvas");
    mapCanvas.setAttribute("width", size[0]);
    mapCanvas.setAttribute("height", size[1]);
    const mapContext = mapCanvas.getContext("2d");

    Array.prototype.forEach.call(
      document.querySelectorAll(`#${mapId} [class*=ol-layer] canvas`),
      (canvas: HTMLCanvasElement) => {
        if (canvas.width > 0) {
          const { opacity } = (canvas.parentNode as HTMLElement).style;
          mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
          const { transform } = canvas.style;
          const matrix = transform
            .match(/^matrix\(([^(]*)\)$/)[1]
            .split(",")
            .map(Number);
          CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
          mapContext.drawImage(canvas, 0, 0);
        }
      },
    );

    let widthRatio = 1;
    let heightRatio = 1;

    const overlayElement = document.querySelector(`#${mapId} div.ol-overlaycontainer`) as HTMLElement;
    if (overlayElement) {
        const overlayCanvas = await html2canvas(overlayElement, {backgroundColor: null, logging: false});
        if (overlayCanvas.width > 0) {
            widthRatio = mapCanvas.width / overlayCanvas.width;
            heightRatio = mapCanvas.height / overlayCanvas.height;
            const matrix = [widthRatio, 0, 0, heightRatio, 0, 0];
            CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
            mapContext.drawImage(overlayCanvas, 0, 0);
        }
    }

    const locationInfoElement = document.getElementsByClassName("location-info")[
      this.mapService.getMapIndex() - 1
    ] as HTMLElement;
    if (locationInfoElement) {
        const locationCanvas = await html2canvas(locationInfoElement, {backgroundColor: null, logging: false});
        if (locationCanvas.width > 0) {
            mapContext.drawImage(
                locationCanvas,
                mapCanvas.width / widthRatio - locationCanvas.width,
                mapCanvas.height / heightRatio - locationCanvas.height,
            );
        }
    }

    return new Promise<Blob>((resolve, reject) => {
      mapCanvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("MapCanvasService: toBlob returned null"));
        },
        "image/png",
      );
    });
  }
}
